import { US_STATES } from "./group.js";
import type { RawRow } from "./types.js";

/**
 * Adapter for Shopify's "Orders" export (Orders → Export in the admin), as
 * opposed to the "Sales"/"Tax" analytics reports the rest of the pipeline was
 * built around.
 *
 * Shape of the Orders export:
 *   - One row per LINE ITEM. Every row repeats `Name`, `Created at` and the
 *     `Lineitem *` columns.
 *   - Order-level fields (Subtotal, Shipping, Taxes, Total, Discount Amount,
 *     Refunded Amount, billing/shipping address, …) appear ONLY on the first
 *     row of each order; later rows leave them blank.
 *   - Tips are exported as a `Lineitem name` of "Tip". They're included in
 *     `Total` but NOT in `Subtotal`.
 *
 * We convert each order into rows using the sales-report column names
 * (`Order name`, `Gross sales`, `Discounts`, …) so `groupOrders` and the
 * workbook builder work unchanged. Order-level money goes on the order's first
 * emitted row only (zeros elsewhere), so summing per order gives the right
 * totals.
 *
 * Mapping (per order):
 *   Gross sales      = Σ qty × price over non-tip line items
 *   Discounts        = Subtotal − Gross sales            (≤ 0, product-level)
 *   Returns          = −Subtotal × refundRatio           (≤ 0)
 *   Net sales        = Subtotal + Returns
 *   Shipping charges = Shipping × (1 − refundRatio)
 *   Duties           = Duties × (1 − refundRatio)
 *   Taxes            = Taxes × (1 − refundRatio)
 *   Total sales      = Net sales + Shipping charges + Duties + Taxes
 * where refundRatio = Refunded Amount / Total (clamped to [0, 1]). The export
 * only gives a single refunded dollar amount, so it's spread pro-rata across
 * product / shipping / tax. Fully refunded orders net to exactly zero.
 * Tips are excluded from sales, matching Shopify's sales reports.
 */

const TIP_NAME = "Tip";

const REQUIRED_HEADERS = ["Name", "Created at", "Lineitem name", "Lineitem quantity", "Lineitem price"];

const STATE_BY_ABBREV: Record<string, string> = Object.fromEntries(
  Object.entries(US_STATES).map(([name, abbrev]) => [abbrev, name]),
);

const COUNTRY_NAMES: Record<string, string> = {
  US: "United States",
};

export function isOrdersExport(rows: RawRow[]): boolean {
  const first = rows[0];
  if (!first) return false;
  if ("Order name" in first) return false;
  return REQUIRED_HEADERS.every((h) => h in first);
}

function num(v: unknown): number {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  const n = Number(String(v).replace(/[$,]/g, "").trim());
  return isNaN(n) ? 0 : n;
}

function str(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

function round2(n: number): number {
  // `|| 0` turns -0 (e.g. -subtotal × 0) into a plain 0.
  return Math.round(n * 100) / 100 || 0;
}

/** Prefer the full province name; fall back to expanding a US state code. */
function regionName(provinceName: unknown, provinceCode: unknown): string {
  const name = str(provinceName);
  if (name) return name;
  const code = str(provinceCode).toUpperCase();
  return STATE_BY_ABBREV[code] ?? code;
}

/** Shopify prefixes zips with `'` so spreadsheet apps keep leading zeros. */
function postalCode(v: unknown): string {
  return str(v).replace(/^'/, "");
}

export function normalizeOrdersExport(rows: RawRow[]): RawRow[] {
  // Group line-item rows by order, preserving first-seen order.
  const orders = new Map<string, RawRow[]>();
  for (const row of rows) {
    const name = str(row["Name"]);
    if (!name) continue;
    let lines = orders.get(name);
    if (!lines) {
      lines = [];
      orders.set(name, lines);
    }
    lines.push(row);
  }

  const out: RawRow[] = [];
  for (const [orderName, lines] of orders) {
    const head = lines[0];
    const items = lines.filter((r) => str(r["Lineitem name"]) !== TIP_NAME);

    const gross = items.reduce((s, r) => s + num(r["Lineitem quantity"]) * num(r["Lineitem price"]), 0);
    const subtotal = num(head["Subtotal"]);
    const shipping = num(head["Shipping"]);
    const taxes = num(head["Taxes"]);
    const duties = num(head["Duties"]);
    const total = num(head["Total"]);
    const refunded = num(head["Refunded Amount"]);
    const refundRatio = total > 0 ? Math.min(Math.max(refunded / total, 0), 1) : 0;
    const keep = 1 - refundRatio;

    const returns = round2(-subtotal * refundRatio);
    const net = round2(subtotal + returns);
    const shippingKept = round2(shipping * keep);
    const dutiesKept = round2(duties * keep);
    const taxesKept = round2(taxes * keep);

    const orderFields = {
      "Order name": orderName,
      Day: head["Created at"],
      "Sale ID": "",
      "Billing region": regionName(head["Billing Province Name"], head["Billing Province"]),
      "Shipping region": regionName(head["Shipping Province Name"], head["Shipping Province"]),
      "Shipping city": str(head["Shipping City"]),
      "Shipping country": COUNTRY_NAMES[str(head["Shipping Country"])] ?? str(head["Shipping Country"]),
      "Shipping postal code": postalCode(head["Shipping Zip"]),
    };

    const orderMoney = {
      Discounts: round2(subtotal - gross),
      Returns: returns,
      "Net sales": net,
      "Shipping charges": shippingKept,
      Duties: dutiesKept,
      "Additional fees": 0,
      Taxes: taxesKept,
      "Total sales": round2(net + shippingKept + dutiesKept + taxesKept),
    };

    const zeroMoney = Object.fromEntries(Object.keys(orderMoney).map((k) => [k, 0]));

    // An order with only a tip (or no items) still needs one row so it shows up.
    const emitted = items.length > 0 ? items : [null];
    emitted.forEach((item, i) => {
      const qty = item ? num(item["Lineitem quantity"]) : 0;
      out.push({
        ...orderFields,
        "Product title at time of sale": item ? str(item["Lineitem name"]) : "",
        Orders: qty,
        "Gross sales": item ? round2(qty * num(item["Lineitem price"])) : 0,
        ...(i === 0 ? orderMoney : zeroMoney),
      });
    });
  }
  return out;
}
